import clsx from "clsx";
import { FC } from "react";

import { AIChatPanel } from "./ai/components/AIChatPanel";
import {
  MOBILE_OVERLAY_ROUTES,
  MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  MobileOverlayRouteType,
} from "./constants";
import { ClassChatSidebarPanel } from "./materials/section/ClassChatSidebarPanel";
import { SectionUsersSidebarPanel } from "./materials/section/SectionUsersSidebarPanel";
import { PlannerSidebarPanel } from "./materials/section/planner/PlannerSidebarPanel";
import { InstructorNotesSidebarPanel } from "./materials/sidebar/InstructorNotesSidebarPanel";
import { UserProfileSidebarPanel } from "./materials/sidebar/UserProfileSidebarPanel";

import { MobileMainMenu } from "components/menus/MobileMainMenu";
import { OVERLAY_TAB_MAP, isFloatingTab } from "helpers/sidebar";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useQueryParamsManager } from "utils/routing";

const MOBILE_OVERLAY_ROUTE_COMPONENTS: Record<MobileOverlayRouteType, FC> = {
  menu: MobileMainMenu,
  users: SectionUsersSidebarPanel,
  profile: UserProfileSidebarPanel,
  instructor_notes: InstructorNotesSidebarPanel,
  planner: PlannerSidebarPanel,
  ai: AIChatPanel,
  chat: ClassChatSidebarPanel,
};

/**
 * This component displays mobile-only routes (like the mobile menu, sidebar views, etc.)
 * over the main content. It needs to be inside a relative container.
 */
export const MobileRoutingOverlay = () => {
  const { getQueryParam, deleteQueryParams } = useQueryParamsManager();
  const isMdOrLarger = useIsMdOrLarger();

  const mobileOverlayRoute = getQueryParam(
    MOBILE_OVERLAY_ROUTE_QUERY_PARAM,
  ) as MobileOverlayRouteType;

  if (!mobileOverlayRoute || !MOBILE_OVERLAY_ROUTES.includes(mobileOverlayRoute)) {
    return null;
  }

  const overlayTab = OVERLAY_TAB_MAP[mobileOverlayRoute];
  const isFloatingOverlay = overlayTab && isFloatingTab(overlayTab);
  if (isFloatingOverlay && isMdOrLarger) {
    return null;
  }

  const isMobileMainMenuOpen = mobileOverlayRoute === "menu";
  const MobileOverlayComponent = MOBILE_OVERLAY_ROUTE_COMPONENTS[mobileOverlayRoute];

  // NOTE: the mobile menu is a special case because its height extends to the top and bottom of the screen,
  // covering the navbar and footer, but its width is fixed to 340px so the underlying content is still visible
  // through the modal backdrop.
  return (
    <>
      {isMobileMainMenuOpen && (
        <div
          className="modal-backdrop"
          onClick={() => deleteQueryParams([MOBILE_OVERLAY_ROUTE_QUERY_PARAM])}
        />
      )}
      <div
        className={clsx("absolute z-[100] bg-white lg:!hidden", {
          "page content-sidebar-panel inset-0 bottom-0": !isMobileMainMenuOpen,
          "-top-[calc(var(--navbar-height)+var(--participant-bar-height))] right-0 h-svh w-[340px]":
            isMobileMainMenuOpen,
          "floating-overlay": isFloatingOverlay,
        })}
      >
        <MobileOverlayComponent />
      </div>
    </>
  );
};
